import { PostgrestError } from '@supabase/supabase-js';
import { useSupabaseMutation } from './useSupabaseMutation';
import { useSupabaseQuery } from './useSupabaseQuery';
import { useSupabaseConnection } from '@/hooks/useSupabaseConnection';
import { queryKeys } from '@/lib/query-client';
import { BOOKING_STATUS } from '@/lib/booking-status';
import { TRIP_STATUS } from '@/lib/trip-status';

// 타입 가드 함수
interface PostgresError {
  code?: string;
  message?: string;
}

const isPostgresError = (error: unknown): error is PostgresError => {
  return typeof error === 'object' && error !== null && 
         ('code' in error || 'message' in error);
};

// Type definitions
export interface CreateTripData {
  product_id: number;
  departure_date: string;
  status: string;
  created_by: string;
}

export interface CreateBookingData {
  trip_id: number;
  num_participants: number;
  total_price: number;
  user_id: string;
  booking_date: string;
  status: string;
}

export interface BookingParticipantData {
  booking_id: number;
  participant_order: number;
  name: string;
  phone: string;
  email?: string | null;
  seat?: number | null;
  gender?: 'male' | 'female' | null;
}

export interface UpdateTripStatusData {
  trip_id: number;
  status: string;
}

export interface ExistingBookingCheck {
  user_id: string;
  trip_id: number;
}

// Response types
export interface Trip {
  trip_id: number;  // 실제 DB 컬럼명과 일치
  product_id: number;
  departure_date: string;
  status: string;
  created_by: string;
  created_at: string;
}

export interface Booking {
  booking_id: number;  // 실제 DB 컬럼명과 일치
  trip_id: number;
  num_participants: number;
  total_price: number;
  user_id: string;
  booking_date: string;
  status: string;
  created_at: string;
}

export interface BookingParticipant {
  id: number;
  booking_id: number;
  participant_order: number;
  name: string;
  phone: string;
  email?: string | null;
  seat?: number | null;
  created_at: string;
}

/**
 * Hook for creating a new trip
 * Creates a trip with 'open' status and sets the creator
 */
export function useCreateTrip() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<Trip, CreateTripData>(
    async (data) => {
      // Fetch product schedule_template to get departure_point
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('schedule_template')
        .eq('product_id', data.product_id)
        .single();
      
      if (productError) {
        console.warn('[useCreateTrip] Failed to fetch product schedule_template:', productError);
      }
      
      // Extract departure_point from the first schedule item
      const departurePoint = product?.schedule_template?.schedule_items?.[0]?.location || null;
      
      const { data: result, error } = await supabase
        .from('trips')
        .insert([{
          product_id: data.product_id,
          departure_date: data.departure_date,
          status: data.status || TRIP_STATUS.OPEN,
          created_by: data.created_by,
          departure_point: departurePoint
        }])
        .select('*')
        .single();

      if (error) throw error;
      return result;
    },
    {
      invalidateQueries: [
        queryKeys.admin.trips(),
        queryKeys.admin.tripsStats(),
      ],
      retry: 2,
      onError: (error) => {
        console.error('Failed to create trip:', error);
      },
    }
  );
}

/**
 * Hook for getting or creating a trip with concurrency safety
 * Handles race conditions when multiple users try to create the same trip
 * Returns existing trip if available, creates new one if not
 */
export function useGetOrCreateTrip() {
  const { supabase } = useSupabaseConnection();
  
  return useSupabaseMutation<Trip, {
    product_id: number;
    departure_date: string;
    created_by: string;
  }>(
    async (data) => {
      // 1. First, try to find an existing bookable trip
      // Include multiple statuses that allow bookings
      const { data: existingTrip, error: searchError } = await supabase
        .from('trips')
        .select('trip_id, status')
        .eq('product_id', data.product_id)
        .eq('departure_date', data.departure_date)
        .in('status', [
          TRIP_STATUS.OPEN,      // 모집중 - accepting bookings
          TRIP_STATUS.PENDING,   // 예약중 - still need more bookings
          TRIP_STATUS.CONFIRMED  // 출발확정 - can still accept if seats available
        ])
        .limit(1)
        .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully
      
      if (existingTrip) {
        console.log('[useGetOrCreateTrip] Reusing existing trip:', existingTrip.trip_id, 'status:', existingTrip.status);
        return existingTrip;
      }
      
      // 2. No existing trip found, create a new one
      console.log('[useGetOrCreateTrip] Creating new trip for date:', data.departure_date);
      
      // Fetch product schedule_template to get departure_point
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('schedule_template')
        .eq('product_id', data.product_id)
        .single();
      
      if (productError) {
        console.warn('[useGetOrCreateTrip] Failed to fetch product schedule_template:', productError);
      }
      
      // Extract departure_point from the first schedule item
      const departurePoint = product?.schedule_template?.schedule_items?.[0]?.location || null;
      
      if (departurePoint) {
        console.log('[useGetOrCreateTrip] Setting departure_point:', departurePoint);
      }
      
      const { data: newTrip, error: insertError } = await supabase
        .from('trips')
        .insert({
          product_id: data.product_id,
          departure_date: data.departure_date,
          status: TRIP_STATUS.OPEN,
          created_by: data.created_by,
          departure_point: departurePoint
        })
        .select('trip_id, status')
        .single();
      
      // 3. Handle unique constraint violation (concurrent creation)
      if (insertError?.code === '23505') {
        console.log('[useGetOrCreateTrip] Concurrent creation detected, re-fetching trip');
        
        // Re-fetch without status filter - accept any existing trip
        // This handles the case where another process created the trip
        // and potentially changed its status already
        const { data: racedTrip, error: raceError } = await supabase
          .from('trips')
          .select('trip_id, status')
          .eq('product_id', data.product_id)
          .eq('departure_date', data.departure_date)
          .limit(1)
          .single();
        
        if (racedTrip) {
          console.log('[useGetOrCreateTrip] Found trip created by concurrent process:', racedTrip.trip_id);
          return racedTrip;
        }
        
        // If we still can't find it, something went wrong
        throw new Error('Failed to create or retrieve trip after race condition');
      }
      
      // Other insert errors
      if (insertError) {
        console.error('[useGetOrCreateTrip] Failed to create trip:', insertError);
        throw insertError;
      }
      
      console.log('[useGetOrCreateTrip] Successfully created new trip:', newTrip?.trip_id);
      return newTrip;
    },
    {
      invalidateQueries: [
        queryKeys.admin.trips(),
        queryKeys.admin.tripsStats(),
      ],
      retry: 1, // Minimal retry for atomic operations
      onError: (error) => {
        console.error('[useGetOrCreateTrip] Operation failed:', error);
      },
    }
  );
}

/**
 * Hook for checking existing booking
 * Used to prevent duplicate bookings for the same user and trip
 */
export function useCheckExistingBooking(params: ExistingBookingCheck, enabled: boolean = false) {
  const { supabase } = useSupabaseConnection();
  return useSupabaseQuery<Booking[], PostgrestError>(
    ['booking-check', params.user_id, params.trip_id],
    async ({ signal }) => {
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', params.user_id)
        .eq('trip_id', params.trip_id)
        .abortSignal(signal);

      if (error) throw error;
      return data;
    },
    {
      enabled,
      staleTime: 0, // Always fresh for duplicate checking
      gcTime: 0, // Don't cache duplicate checks
      retry: 1, // Minimal retry for checks
      onError: (error) => {
        console.error('Failed to check existing booking:', error);
      },
    }
  );
}

/**
 * Hook for creating a new booking
 * Creates booking with 'pending' status by default
 */
export function useCreateBooking() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<Booking, CreateBookingData>(
    async (data) => {
      const { data: result, error } = await supabase
        .from('bookings')
        .insert([{
          trip_id: data.trip_id,
          num_participants: data.num_participants,
          total_price: data.total_price,
          user_id: data.user_id,
          booking_date: data.booking_date,
          status: data.status || BOOKING_STATUS.PENDING,
        }])
        .select('*')
        .single();

      if (error) throw error;
      return result;
    },
    {
      invalidateQueries: [
        queryKeys.reservations.all(),
        queryKeys.admin.trips(),
        queryKeys.admin.tripsStats(),
      ],
      retry: 2,
      onError: (error: unknown) => {
        console.error('Failed to create booking:', error);
        
        // 중복 예약 에러를 더 친화적으로 처리
        if (isPostgresError(error) && error.code === '23505' && error.message?.includes('unique_user_trip_booking')) {
          const friendlyError = new Error('이미 이 여행에 예약하셨습니다. 예약 내역을 확인해 주세요.');
          Object.assign(friendlyError, { code: 'DUPLICATE_BOOKING' });
          throw friendlyError;
        }
      },
    }
  );
}

/**
 * Hook for atomically checking for existing booking and creating if none exists
 * This prevents race conditions by using proper async/await instead of setTimeout
 */
export function useCreateBookingIfNotExists() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<{ existing?: Booking; created?: Booking }, CreateBookingData>(
    async (data) => {
      // First, check for existing booking
      const { data: existingBookings, error: checkError } = await supabase
        .from('bookings')
        .select('*')
        .eq('user_id', data.user_id)
        .eq('trip_id', data.trip_id)
        .limit(1);

      if (checkError) throw checkError;

      // If booking already exists, return it
      if (existingBookings && existingBookings.length > 0) {
        return { existing: existingBookings[0] };
      }

      // If no existing booking, create new one
      const { data: result, error } = await supabase
        .from('bookings')
        .insert([{
          trip_id: data.trip_id,
          num_participants: data.num_participants,
          total_price: data.total_price,
          user_id: data.user_id,
          booking_date: data.booking_date,
          status: data.status || BOOKING_STATUS.PENDING,
        }])
        .select('*')
        .single();

      if (error) {
        // Handle the unique constraint violation gracefully
        if (error.code === '23505' && error.message.includes('unique_user_trip_booking')) {
          // Another process created a booking between our check and insert
          // Re-fetch the existing booking
          const { data: refetchedBookings, error: refetchError } = await supabase
            .from('bookings')
            .select('*')
            .eq('user_id', data.user_id)
            .eq('trip_id', data.trip_id)
            .limit(1);
          
          if (refetchError) throw refetchError;
          
          if (refetchedBookings && refetchedBookings.length > 0) {
            return { existing: refetchedBookings[0] };
          }
        }
        throw error;
      }

      return { created: result };
    },
    {
      invalidateQueries: [
        queryKeys.reservations.all(),
        queryKeys.admin.trips(),
        queryKeys.admin.tripsStats(),
      ],
      retry: 1, // Only retry once for atomic operations
      onError: (error) => {
        console.error('Failed to create booking atomically:', error);
      },
    }
  );
}

/**
 * Hook for creating booking participants
 * Handles bulk insertion of participant data with proper ordering
 */
export function useCreateBookingParticipants() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<BookingParticipant[], BookingParticipantData[]>(
    async (participants) => {
      if (!Array.isArray(participants) || participants.length === 0) {
        throw new Error('Participants data is required and must be non-empty array');
      }
      
      const orderedParticipants = participants.map((participant, index) => ({
        ...participant,
        participant_order: participant.participant_order || index + 1,
      }));
      
      const { data, error } = await supabase
        .from('booking_participants')
        .insert(orderedParticipants)
        .select('*');

      if (error) {
        // 좌석 중복 에러를 더 친화적으로 처리
        if (error.code === '23505' && (error.message?.includes('Seat') || error.message?.includes('seat'))) {
          const seatMatch = error.message.match(/Seat (\d+)|seat (\d+)/i);
          const seatNumber = seatMatch ? (seatMatch[1] || seatMatch[2]) : '선택한';
          
          // 친화적인 에러 객체 생성
          const friendlyError = new Error(`앗! ${seatNumber}번 좌석을 다른 분이 방금 선택하셨어요 😊`);
          Object.assign(friendlyError, { 
            code: 'SEAT_ALREADY_TAKEN',
            seatNumber: seatNumber,
            isFriendlyError: true // 프론트엔드에서 특별 처리를 위한 플래그
          });
          throw friendlyError;
        }
        // 3번 좌석 예약 시도 에러
        if (error.message?.includes('Seat 3 is reserved')) {
          const friendlyError = new Error('3번 좌석은 인솔자 전용 좌석입니다 🚌');
          Object.assign(friendlyError, { 
            code: 'RESERVED_SEAT',
            isFriendlyError: true
          });
          throw friendlyError;
        }
        throw error;
      }
      return data;
    },
    {
      invalidateQueries: [
        queryKeys.reservations.all(),
        // 좌석 정보도 즉시 무효화하여 최신 상태 유지
        ['bookedSeats'],
      ],
      retry: 0, // 좌석 예약은 재시도하지 않음 (중복 방지)
      onError: (error) => {
        console.error('Failed to create booking participants:', error);
      },
    }
  );
}

/**
 * Hook for updating trip status
 * Updates trip status and invalidates relevant caches
 */
export function useUpdateTripStatus() {
  const { supabase } = useSupabaseConnection();
  return useSupabaseMutation<Trip, UpdateTripStatusData>(
    async (data) => {
      const validStatuses = Object.values(TRIP_STATUS) as string[];
      if (!validStatuses.includes(data.status)) {
        throw new Error(`Invalid trip status: ${data.status}. Must be one of: ${validStatuses.join(', ')}`);
      }
      
      const { data: result, error } = await supabase
        .from('trips')
        .update({ status: data.status })
        .eq('trip_id', data.trip_id)
        .select('*')
        .single();

      if (error) throw error;
      return result;
    },
    {
      invalidateQueries: [
        queryKeys.admin.trips(),
        queryKeys.admin.tripsStats(),
      ],
      retry: 2,
      onError: (error) => {
        console.error('Failed to update trip status:', error);
      },
    }
  );
}

/**
 * Compound mutation hook for complete booking flow
 * Handles the entire booking creation process with proper error handling
 * and rollback capabilities
 */
export function useCompleteBookingFlow() {
  const createTrip = useCreateTrip();
  const createBooking = useCreateBooking();
  const createParticipants = useCreateBookingParticipants();
  const updateTripStatus = useUpdateTripStatus();

  return {
    // Individual mutations
    createTrip,
    createBooking,
    createParticipants,
    updateTripStatus,
    
    // Combined status
    isLoading: createTrip.isPending || createBooking.isPending || 
               createParticipants.isPending || updateTripStatus.isPending,
    
    // Combined error
    error: createTrip.error || createBooking.error || 
           createParticipants.error || updateTripStatus.error,
    
    // Reset all mutations
    reset: () => {
      createTrip.reset();
      createBooking.reset();
      createParticipants.reset();
      updateTripStatus.reset();
    },
    
    // Success states
    isSuccess: {
      tripCreated: createTrip.isSuccess,
      bookingCreated: createBooking.isSuccess,
      participantsCreated: createParticipants.isSuccess,
      statusUpdated: updateTripStatus.isSuccess,
    },
    
    // Data results
    data: {
      trip: createTrip.data,
      booking: createBooking.data,
      participants: createParticipants.data,
      updatedTrip: updateTripStatus.data,
    },
  };
}

// Re-export types for external use
export type {
  Trip,
  Booking,
  BookingParticipant,
  CreateTripData,
  CreateBookingData,
  BookingParticipantData,
  UpdateTripStatusData,
  ExistingBookingCheck,
};